import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { selectDevicesAppsByGroupIds } from "@dashboard/devices/store/selectors/devices";
import { useAllCurrentWorkspaceFiles, useCurrentWorkspaceFiles } from "@dashboard/files/api/helpers";
import { FileContentType } from "@dashboard/files/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { expandGroupIdsWithDescendants } from "@dashboard/groups/utils";
import LicenseSelectors from "@dashboard/licenses/store/selectors";
import { Paper, TableContainer, TableRow, Theme, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UserSettingsService from "@services/userSettingsService";
import { CustomTileContainer, CustomTileTitle, HeaderContainer } from "@shared/CustomTile";
import { LoadingIcon } from "@shared/Loading";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import TablePagination from "@shared/table/TablePagination";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import TableItem from "./TableItem";

export interface TransformedApp {
    label: string;
    package: string;
    versionCode: number;
    versionName: string;
    count?: number;
}

const useStyles = makeStyles()((muiTheme: Theme) => ({
    container: { height: "100%" },
    table: { marginTop: muiTheme.spacing(3), boxShadow: "none" },
}));

const getUniqueKey = (app: TransformedApp) => `${app.package}-${app.versionCode}`;

const mergeApps = (installedApps: TransformedApp[], sharedApks: API.File[]) => {
    const appsMap = new Map<string, TransformedApp>();

    installedApps.forEach((app) => {
        const key = getUniqueKey(app);
        const existingApp = appsMap.get(key);
        if (existingApp) {
            existingApp.count = (existingApp.count || 0) + 1;
        } else {
            appsMap.set(key, { ...app, count: 1 });
        }
    });

    sharedApks.forEach((file) => {
        const apk: TransformedApp = {
            label: file?.metadata?.file?.apk?.badging?.label || file?.filename || "",
            package: file?.metadata?.file?.apk?.manifest?.package || "",
            versionCode: Number(file.metadata?.file?.apk?.manifest?.versioncode || ""),
            versionName: file.metadata?.file?.apk?.manifest?.versionname || "",
            count: 0,
        };
        const key = getUniqueKey(apk);
        if (!appsMap.has(key)) {
            appsMap.set(key, apk);
        }
    });

    return Array.from(appsMap.values());
};

const columnHelper = createColumnHelper<TransformedApp>();

function getColumns(theme: Theme) {
    return [
        columnHelper.accessor("label", {
            id: "label",
            header: i18next.t("pages:overviewPage.table.header.application"),
            cell: (info) => (
                <MinimalTableCell style={{ borderBottom: `1px solid ${theme.palette.emteria.border.secondary}` }}>
                    <Typography>{info.row.original.label}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("versionCode", {
            id: "versionCode",
            header: i18next.t("pages:overviewPage.table.header.version"),
            cell: (info) => (
                <MinimalTableCell style={{ borderBottom: `1px solid ${theme.palette.emteria.border.secondary}` }}>
                    <Typography>{info.getValue<string>()}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                const version = info.row.original.versionCode;
                const count = info.getValue<number>();
                return (
                    <TableItem
                        count={count}
                        version={version}
                        apps={info.row.original}
                        filteredGroupIds={info.table.options.meta?.filteredGroupIds || []}
                    />
                );
            },
            meta: {
                align: "right",
            },
        }),
    ];
}

type Props = {
    groupIds: number[];
};

export const ManagedAppsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const theme = useTheme();

    const [sorting, setSorting] = useState<SortingState>([
        { id: "count", desc: true },
        { id: "label", desc: false },
    ]);
    const groups = useSelector(selectGroups);
    const filteredGroupIds = React.useMemo(() => {
        const base = !groupIds.length ? groups.map((group) => group.id) : groupIds;
        return expandGroupIdsWithDescendants(base, groups);
    }, [groupIds, groups]);

    // Reuse the prefetched/Files-page query so navigating to /dashboard/files
    // hits the cache. Read the same params source (UserSettingsService) and
    // filter for APKs client-side. This is a temporary stop-gap until APK
    // info is exposed via group statistics; if the first page happens to
    // contain no APKs, fall back to a dedicated APK query so the card still
    // shows results when APKs exist further down the file list.
    const filePageParams = React.useMemo(() => UserSettingsService.getFilePageParams(), []);
    const { data: filesData, isLoading: isLoadingFiles } = useCurrentWorkspaceFiles(filePageParams);
    const apksFromPage = React.useMemo(
        () =>
            (filesData?.list ?? []).filter(
                (file) => file.contentType === FileContentType.Apk || file.contentType === FileContentType.ApkV2,
            ),
        [filesData?.list],
    );

    const needsApkFallback = !isLoadingFiles && (filesData?.count ?? 0) > 0 && apksFromPage.length === 0;
    const { data: apkFallbackData, isFetching: isLoadingApkFallback } = useAllCurrentWorkspaceFiles(
        { contentTypes: [FileContentType.Apk, FileContentType.ApkV2] },
        { skip: !needsApkFallback },
    );

    const sharedApks = apksFromPage.length > 0 ? apksFromPage : apkFallbackData;
    const installedApps = useSelector((state) => selectDevicesAppsByGroupIds(state, filteredGroupIds), shallowEqual);
    const mergedApps = React.useMemo(() => mergeApps(installedApps, sharedApks), [installedApps, sharedApks]);

    const licenseStatus = useSelector(LicenseSelectors.selectLicensesStatus);
    const isLoading = licenseStatus === "pending" || isLoadingFiles || isLoadingApkFallback;

    const table = useReactTable<TransformedApp>({
        data: mergedApps,
        columns: getColumns(theme),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <CustomTileContainer className={classes.container}>
            <HeaderContainer>
                <CustomTileTitle>{t("pages:overviewPage.cards.managedApps.title")}</CustomTileTitle>
                {isLoading && (
                    <Tooltip title={t("pages:overviewPage.tooltips.loadingMoreData")} arrow>
                        <span>
                            <LoadingIcon warning />
                        </span>
                    </Tooltip>
                )}
            </HeaderContainer>
            <TableContainer component={Paper} className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody table={table} noDataMessage={t("pages:overviewPage.cards.managedApps.noApplications")}>
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <TablePagination
                component="div"
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
                slotProps={{
                    actions: {
                        nextButton: { title: "" },
                        previousButton: { title: "" },
                    },
                }}
            />
        </CustomTileContainer>
    );
};
